import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth";
import { checkForms, getClientByEmail } from "../../services/clientService";
import { Client } from "../../interfaces/client";
import { getAllOrdersByClientIDWithParams } from "../../services/orderService";
import { SimpleOrder } from "../../interfaces/simpleOrder";
import ToolTip from "../../components/component-tools/ToolTip";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../services/helpers";
import { ClientNotification } from "../../interfaces/clientNotification";
import { getNotifications } from "../../services/notificationsService";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";

const CurrentOrders = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const { decodeToken } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [orders, setOrders] = useState<SimpleOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastOrderRef = useRef<HTMLDivElement | null>(null);
  const [showAbbreviation, setShowAbbreviation] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string | null>("All");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchApplied, setSearchApplied] = useState("");

  const handleSearch = () => {
    if (
      loading ||
      searchTerm.trim() === searchApplied ||
      (searchTerm.trim().length < 2 && searchTerm.trim().length >= 1)
    )
      return;

    setSearchApplied(searchTerm.trim());
    setOrders([]);
    setPageNumber(1);
    setHasMore(true);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchApplied("");
    setOrders([]);
    setPageNumber(1);
    setHasMore(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const clientData = await getClientByEmail(email, clientID);
        setClient(clientData);

        const [showAbbreviationResponse] = await checkForms(
          clientData.clientID,
        );
        setShowAbbreviation(showAbbreviationResponse);

        if (clientData.clientID) {
          const { orders: newOrders, totalRecords } =
            await getAllOrdersByClientIDWithParams(
              activeFilter || "",
              searchApplied,
              clientData.clientID,
              pageNumber,
              10,
            );

          setOrders((prevOrders) => {
            const existingIds = new Set(
              prevOrders.map((order) => order.orderID),
            );
            const uniqueOrders = newOrders.filter(
              (order) => !existingIds.has(order.orderID),
            );
            const combinedOrders = [...prevOrders, ...uniqueOrders];

            setHasMore(combinedOrders.length < totalRecords);
            return pageNumber === 1 ? newOrders : combinedOrders;
          });
        }

        const notificationsResponse = await getNotifications(
          clientData?.clientID,
        );
        notificationsResponse.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setNotifications(notificationsResponse);

        const unreadCount = notificationsResponse.length;
        setUnreadCount(unreadCount);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setErrorMessage("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [pageNumber, activeFilter, client?.clientID, decodeToken, searchApplied]);

  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 },
    );

    if (lastOrderRef.current) observer.current.observe(lastOrderRef.current);

    return () => observer.current?.disconnect();
  }, [hasMore, loading]);

  const handleFilterChange = (newFilter: string) => {
    if (newFilter === activeFilter) {
      setActiveFilter("all");
    } else {
      setActiveFilter(newFilter);
    }

    setOrders([]);
    setPageNumber(1);
    setHasMore(true);
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Home</h1>
        <div className="page-items-container">
          <div className="field-long">
            <label className="field-label">Client Name:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.clientName || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Abbreviation/Client Number:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={
                  showAbbreviation
                    ? client?.abbreviation || ""
                    : "not available."
                }
                disabled
                readOnly
              />
            </div>
          </div>
          <button
            className="notification-button"
            onClick={() => setShowNotifications(true)}
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span className="notification-dot">{unreadCount}</span>
            )}
          </button>
        </div>
        {showNotifications && client?.clientID && (
          <NotificationsPopup
            notificationsInput={notifications}
            clientID={client?.clientID}
            onClose={(
              newUnreadCount = unreadCount,
              newNotifications = notifications,
            ) => {
              setNotifications(newNotifications);
              setShowNotifications(false);
              setUnreadCount(newUnreadCount);
            }}
          />
        )}
      </div>

      {loading && pageNumber === 1 ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {(client?.createdDate == null ||
            new Date(client.createdDate) <= new Date("2025-07-08")) && (
            <>
              <div className="text-container">
                <b>Please note:</b> We’ve transitioned to a new system as of
                Summer 2025. While we migrated data as accurately as possible,
                differences in field formatting may cause some discrepancies.
                All migrated candidate orders are marked with an asterisk (*) -
                final report dates and candidate details are correct, but
                formatting may vary. In the previous system, reference and
                criminal checks were processed separately, so you may see two
                orders for the same candidate. For the most accurate historical
                information, please refer to the original reports provided by
                Oxford prior to the system change.
              </div>
              <br></br>
            </>
          )}
          {!showAbbreviation && (
            <div className="text-container error">
              Please complete necessary forms in order to start ordering
              background screening and referencing. Then your
              abbreviation/client number will become visible.
            </div>
          )}
          <div className="text-container">
            <div className="text-header">
              Instructions to order services from Oxford Document Management:
            </div>
            <div className="text-body">
              If you have defined Position Codes, your candidate can complete
              the required forms online. For them to do so, please provide the
              following instructions below for returning and new candidates.
              <ul>
                <li>
                  If you are a <b>returning candidate</b> that has previously
                  logged into the Oxford system:
                  <ol>
                    <li>
                      Log in at{" "}
                      <a href="https://www.oxfordportal.com/">
                        https://www.oxfordportal.com/
                      </a>{" "}
                      by selecting “Candidate” and entering your email address
                      used for a previous search with Oxford.
                    </li>
                    <li>
                      Once logged in, click “New Order” in the top right corner
                      and enter the Client Number/Abbreviation:{" "}
                      <b>
                        {showAbbreviation
                          ? client?.abbreviation || ""
                          : "not available"}
                      </b>{" "}
                      and the Position Code{" "}
                      <b>(INSERT REQUIRED POSITION CODE NUMBER).</b>{" "}
                    </li>
                  </ol>
                </li>
                <li>
                  If you are a <b>new candidate</b> that has never logged into
                  the Oxford system:
                  <ol>
                    <li>
                      Navigate to{" "}
                      <a href="https://www.oxfordportal.com/">
                        https://www.oxfordportal.com/
                      </a>
                      .{" "}
                    </li>
                    <li>
                      Click “New here? Click here to register!”, select
                      “Candidate” and “Go to Registration.”
                    </li>
                    <li>
                      Fill in your name, email, as well as the Client
                      Number/Abbreviation{" "}
                      <b>
                        {showAbbreviation
                          ? client?.abbreviation || ""
                          : "not available"}
                      </b>{" "}
                      and the Position Code{" "}
                      <b>(INSERT REQUIRED POSITION CODE NUMBER).</b>{" "}
                    </li>
                  </ol>
                </li>
              </ul>
            </div>
            {showAbbreviation &&
              client?.abbreviation &&
              /[a-zA-Z]/.test(client.abbreviation) && (
                <div className="text-body">
                  If you want to start a background check without using position
                  codes you can email request@oxforddoc.com and provide the
                  candidate’s name, email address, and reason for the background
                  check. We will respond as soon as possible.
                </div>
              )}
          </div>
          <div className="divider-small"></div>
          <div
            className="page-items-container"
            style={{ justifyContent: "space-between" }}
          >
            <div className="text-header">
              List of All Candidates{" "}
              <ToolTip
                tooltipText={`Blue: Criminal\nOrange: Reference`}
                placement="right"
                className="info-icon"
              />
            </div>

            <div className="search-container">
              <input
                style={{ border: "none", width: "100%", outline: "none" }}
                type="text"
                placeholder="Search Candidate Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              {searchTerm.trim() ? (
                <i
                  className="bi bi-x-circle clear-icon"
                  onClick={handleClearSearch}
                  style={{ cursor: loading ? "disabled" : "pointer" }}
                ></i>
              ) : (
                <i
                  className="bi bi-search search-icon"
                  onClick={handleSearch}
                  style={{ cursor: loading ? "disabled" : "pointer" }}
                ></i>
              )}
            </div>
          </div>
          <br></br>

          <div className="orders-container">
            {orders.length > 0 ? (
              <>
                <div className="orders-list">
                  {orders.map((order, index) => {
                    const isClickable = ![1].includes(order.orderStatusID);

                    return (
                      <div
                        key={order.orderID}
                        ref={index === orders.length - 1 ? lastOrderRef : null}
                        className="order-card-small"
                        style={{
                          cursor: isClickable ? "pointer" : "not-allowed",
                        }}
                        onClick={() =>
                          isClickable &&
                          navigate(`/client/view-order/${order.orderID}`)
                        }
                      >
                        <div className="order-card-small-info">
                          <div
                            className="candidate-name"
                            style={{ flex: "0 0 40%" }}
                          >
                            {(order.applican_link || order.candidat_link) &&
                              "*"}
                            {order.candidateFirstName} {order.candidateLastName}
                            <div className="candidate-ssn">
                              {order.designation}
                            </div>
                          </div>
                          <div className="reference-contacted">
                            <div className="hidden">Initiated:</div>
                            <div className="chip-date">
                              {formatDate(order.initiatedDate, "us")}
                            </div>
                          </div>
                          <div className="order-status-indicator">
                            <div
                              className={`circle-status 
      ${order.hasReferenceOrder ? "has-reference" : "no-reference"} 
      ${order.referenceInAdminReview ? "reference-active" : ""} 
      ${order.hasCriminalOrder ? "has-criminal" : "no-criminal"} 
      ${order.criminalInAdminReview ? "criminal-active" : ""}`}
                            ></div>
                          </div>
                        </div>
                        <div
                          className={`chip hidden ${
                            [2, 3].includes(order.orderStatusID)
                              ? "in-progress"
                              : [1].includes(order.orderStatusID)
                                ? "not-responded"
                                : [6].includes(order.orderStatusID)
                                  ? "status-subscription"
                                  : "report-ready"
                          }`}
                        >
                          {order.orderStatusID === 4
                            ? "Completed"
                            : order.orderStatusID === 1
                              ? "Not Responded"
                              : order.orderStatusID === 6
                                ? "Subscription"
                                : "In Process"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-sub-header">No candidate orders found.</div>
            )}
            <div className="filter-container">
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => handleFilterChange("")}
                  className={`chip all ${
                    activeFilter === "" ? "active-filter" : ""
                  }`}
                >
                  All Orders
                </button>
                <ToolTip
                  tooltipText="All Orders regardless of status."
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => handleFilterChange("NotResponded")}
                  className={`chip not-responded ${
                    activeFilter === "NotResponded" ? "active-filter" : ""
                  }`}
                >
                  Not Responded
                </button>
                <ToolTip
                  tooltipText="Candidate has not submitted forms to start the process"
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => handleFilterChange("InProgress")}
                  className={`chip in-progress ${
                    activeFilter === "InProgress" ? "active-filter" : ""
                  }`}
                >
                  In Process
                </button>
                <ToolTip
                  tooltipText="Candidate has submitted the forms and is in process"
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => handleFilterChange("Subscription")}
                  className={`chip status-subscription ${
                    activeFilter === "Subscription" ? "active-filter" : ""
                  }`}
                >
                  Subscription
                </button>
                <ToolTip
                  tooltipText="Order has an active subscription service"
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => handleFilterChange("Completed")}
                  className={`chip report-ready ${
                    activeFilter === "Completed" ? "active-filter" : ""
                  }`}
                >
                  Completed
                </button>
                <ToolTip
                  tooltipText="All services are completed"
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
            </div>
          </div>
          {loading && pageNumber > 1 && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div className="loading-spinner-small"></div>
            </div>
          )}
        </>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default CurrentOrders;
